/**
 * Bottom-left "0:12 / 1:45" overlay — playback position and total duration.
 *
 * Purely informational: it renders no touch target of its own, and its parent
 * slot in `VideoPlayer` is marked `pointerEvents="none"` so presses fall
 * through to the surface's tap-to-toggle and the scrub bar it sits above.
 */
import { memo, type FC } from "react";
import { View } from "react-native";
import "../../../theme";
import { StyleSheet } from "react-native-unistyles";
import { Text } from "../../Text";

export interface VideoPlayerTimeBubbleProps {
  /** Playback position, in seconds. */
  readonly currentTime: number;
  /** Total duration, in seconds. The bubble is hidden while this is 0. */
  readonly duration: number;
}

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Formats `seconds` as `m:ss`, widening to `h:mm:ss` once `shapeSeconds`
 * crosses an hour.
 *
 * The shape is taken from a *separate* value (always the clip's duration, for
 * both halves of the label) rather than from `seconds` itself so the field
 * count is fixed for the whole clip: shaping each half against its own value
 * would render the position of a 1h20m clip as `9:59` right up to the hour
 * mark and then jump to `1:00:00`, resizing the bubble mid-playback.
 */
function formatPlaybackTime(seconds: number, shapeSeconds: number): string {
  // Guards against the non-finite/negative values a playback engine can report
  // before metadata loads (`NaN` duration, a briefly negative currentTime).
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const minutes = Math.floor((safeSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const remainderSeconds = safeSeconds % SECONDS_PER_MINUTE;

  if (shapeSeconds >= SECONDS_PER_HOUR) {
    const hours = Math.floor(safeSeconds / SECONDS_PER_HOUR);
    return `${String(hours)}:${pad(minutes)}:${pad(remainderSeconds)}`;
  }
  return `${String(minutes)}:${pad(remainderSeconds)}`;
}

const VideoPlayerTimeBubble: FC<VideoPlayerTimeBubbleProps> = memo(({ currentTime, duration }) => {
  // Same rule as the scrub bar: with no known duration there is no meaningful
  // total to show, so the bubble stays out of the way entirely.
  if (!(duration > 0)) return null;

  // Engines routinely report a position a fraction past the duration on the
  // final frame — clamped so the bubble never claims 1:01 of a 1:00 clip.
  const positionLabel = formatPlaybackTime(Math.min(currentTime, duration), duration);
  const durationLabel = formatPlaybackTime(duration, duration);

  return (
    <View style={styles.bubble}>
      {/* The visible label uses "/" for compactness; the accessibility label
          spells it out so a screen reader doesn't announce "slash". */}
      <Text textStyle="xs/medium" color="white" accessibilityLabel={`${positionLabel} of ${durationLabel}`}>
        {positionLabel} / {durationLabel}
      </Text>
    </View>
  );
});

VideoPlayerTimeBubble.displayName = "VideoPlayerTimeBubble";

export { VideoPlayerTimeBubble };

const styles = StyleSheet.create((theme) => ({
  bubble: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing.xl,
    borderCurve: "continuous",
    // Sits over the video frame, so it needs its own backdrop to stay legible
    // against a bright scene — same token as the carousel's overlay chrome.
    backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
  },
}));
